/**
 * AIInsightsModal — dialog full-width col contenuto strutturato del report AI.
 * Si monta solo quando viene aperto, così il rendering pesante (NegativeCard
 * + textarea controllate) non accade in background.
 *
 * Estratto da `AIInsightsPanel.js` il 2026-05-29 per ridurre la complessità
 * ciclomatica del file principale. API/data-testid invariati.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Sparkles, AlertTriangle, Clock, TrendingUp, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { AIInsightsNegativeCard } from './AIInsightsNegativeCard';


function PositiveCard({ index, positive }) {
  return (
    <div
      className="border border-emerald-200 bg-emerald-50/50 p-3"
      data-testid={`ai-positive-${index}`}
    >
      <div className="flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-900 leading-tight">{positive.title}</p>
          <p className="text-xs text-slate-700 mt-1 leading-snug">{positive.description}</p>
        </div>
      </div>
    </div>
  );
}


export function AIInsightsModal({
  open, onOpenChange, title, data, isOps, scope, entityId, storeId,
  isStale, generatedAtLabel,
}) {
  const structured = data?.structured;
  const positives = structured?.positives || [];
  const negatives = structured?.negatives || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-none p-0"
        data-testid="ai-insights-modal"
      >
        <DialogHeader className="px-5 py-4 border-b border-slate-200">
          <DialogTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" /> {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            3 punti di forza + 3 criticità con suggerimenti operativi e piani d'azione.
          </DialogDescription>
        </DialogHeader>

        {isStale && (
          <div className="px-5 py-3 bg-orange-50 border-b border-orange-200 flex items-start gap-2" data-testid="ai-stale-warning">
            <AlertTriangle className="w-4 h-4 text-orange-700 shrink-0 mt-0.5" />
            <p className="text-xs text-orange-900">
              <span className="font-semibold">Dati modificati dopo la generazione.</span> Chiudi e clicca <span className="font-semibold">Rigenera</span> per un'analisi aggiornata.
            </p>
          </div>
        )}

        <div className="px-5 py-5">
          <section className="mb-6">
            <h4 className="font-heading text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" /> Punti di forza del periodo
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ai-positives">
              {positives.length === 0 && <p className="text-xs text-slate-400 col-span-3">Nessun punto di forza individuato.</p>}
              {positives.map((p, i) => (
                <PositiveCard key={p.title || `pos-${i}`} index={i} positive={p} />
              ))}
            </div>
          </section>

          <section>
            <h4 className="font-heading text-xs font-semibold uppercase tracking-wider text-red-700 mb-3 flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5" /> Criticità da affrontare
            </h4>
            <div className="space-y-3" data-testid="ai-negatives">
              {negatives.length === 0 && <p className="text-xs text-slate-400">Nessuna criticità individuata.</p>}
              {negatives.map((n, i) => (
                <AIInsightsNegativeCard
                  key={n.title || `neg-${i}`}
                  index={i}
                  negative={n}
                  isOps={isOps}
                  scope={scope}
                  entityId={entityId}
                  storeId={storeId}
                  signature={data?.signature}
                  existingAction={data?.am_actions?.[i]}
                />
              ))}
            </div>
          </section>

          <p className="text-[11px] text-slate-400 mt-5 pt-3 border-t border-slate-100 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Generato il <span className="font-medium text-slate-600">{generatedAtLabel}</span> · {data?.model}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
